/**
 * useConnectorStatus — the one shared poll over the `GET /api/settings/connector-status`
 * health feed. It owns the whole best-effort lifecycle (initial fetch, a 60s
 * interval, a window-focus refetch, a cancelled-guard on unmount, and an
 * error-swallow that keeps the last known rows on a transient blip) and returns
 * the raw typed rows.
 *
 * Both the workspace-wide attention signal (useConnectorAlerts) and the per-source
 * desk-chip dot map (useConnectorGlyphStatus) are now cheap selectors over this
 * one hook, so the fetch / interval / focus / swallow skeleton lives in exactly
 * one place and the backend row shape is typed once here. There is no global
 * query cache in the app, so a tiny poll like this is simpler than threading the
 * feed through a provider.
 *
 * Returns an empty array until the first read settles, and never throws into the
 * render tree.
 */
import { useEffect, useState } from "react";

export const CONNECTOR_STATUS_POLL_MS = 60_000;

export type ConnectorHealthState = "healthy" | "stale" | "error";

/** Mirrors the backend `ConnectorIssueReason` (connectorStatus.ts). Additive. */
export type ConnectorIssueReason =
  | "stuck_backfill"
  | "zero_ingest"
  | "sync_error"
  | "stale_sync";

/** One row of the connector-status feed. Typed once; selectors read from here. */
export interface ConnectorStatusRow {
  /** Canonical source key, e.g. "gcal", "gmail", "notion". */
  source: string;
  /** Human-readable connector label, e.g. "Google Calendar". */
  label: string;
  state: ConnectorHealthState;
  /** Precise reason code, when the backend supplies one. */
  reason?: ConnectorIssueReason;
  /** Raw upstream error — never render this verbatim in a shared surface. */
  lastError?: { message: string; at: string };
}

export function useConnectorStatus(
  pollIntervalMs: number = CONNECTOR_STATUS_POLL_MS,
): readonly ConnectorStatusRow[] {
  const [rows, setRows] = useState<readonly ConnectorStatusRow[]>([]);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const response = await fetch("/api/settings/connector-status", {
          credentials: "include",
        });
        if (!response.ok) return;
        const data = (await response.json()) as ConnectorStatusRow[];
        if (cancelled || !Array.isArray(data)) return;
        setRows(data);
      } catch {
        // Health is best-effort; keep the last known rows on a transient blip.
      }
    }

    void load();
    const timer = window.setInterval(load, pollIntervalMs);
    const onFocus = () => void load();
    window.addEventListener("focus", onFocus);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
      window.removeEventListener("focus", onFocus);
    };
  }, [pollIntervalMs]);

  return rows;
}
